import * as React from "react";
import { CalendarDays, Clock } from "lucide-react";

import { cn } from "@/lib/utils";

import { DayRange, DayRangeCalendar } from "./DayRangeCalendar";
import { bandIsUsable, TimeBand, TimeRangePicker } from "./TimeRangePicker";
import {
  durationMinutes,
  formatDateLabel,
  formatTime,
  MEETING_STATE_STYLES,
  meetingTitle,
  availabilityPatternLabel,
  participantStateChip,
  rescheduleGaveUpBanner,
  ReschedulePick,
  shortId,
  UiMeeting,
} from "./shared";

export function MeetingListRow({
  meeting,
  onOpenNegotiation,
  onCancel,
  onReschedule,
  onAbandonReschedule,
  onResolve,
}: {
  meeting: UiMeeting;
  onCancel?: (id: string) => void;
  onReschedule?: (id: string, pick: ReschedulePick) => void;
  /** Stop moving this meeting and keep the time it already has. */
  onAbandonReschedule?: (id: string) => void;
  /** Open the negotiation drawer for this meeting. */
  onOpenNegotiation?: (id: string) => void;
  onResolve?: (id: string, startTimes: string[]) => void;
}) {
  const style = MEETING_STATE_STYLES[meeting.state];
  const when = meeting.start ?? meeting.createdAt;
  const duration = durationMinutes(meeting);
  const typeLabel = availabilityPatternLabel(meeting.availabilityPattern);
  const actionable = meeting.state === "OPEN" || meeting.state === "ACCEPTED";
  const cancellable = !!onCancel && actionable;
  const reschedulable = !!onReschedule && actionable;
  const atImpasse = meeting.state === "AWAITING_INITIATOR";
  const resolvable =
    !!onResolve && atImpasse && meeting.counterOptions.length > 0;
  // Per-participant chips only earn their space on group meetings (2+ peers).
  const showStates = meeting.participantStates.length > 1;


  const [picking, setPicking] = React.useState(false);
  const [days, setDays] = React.useState<DayRange | null>(null);
  // "Same time, different day" is the overwhelmingly common move, so the band
  // starts as the meeting's own hours rather than empty.
  const currentBand = React.useMemo<TimeBand | null>(
    () =>
      meeting.start && meeting.end
        ? { start: formatTime(meeting.start), end: formatTime(meeting.end) }
        : null,
    [meeting.start, meeting.end],
  );
  const [band, setBand] = React.useState<TimeBand | null>(currentBand);
  // Only the creator sees the toggle, so only their default can be strict.
  const [strict, setStrict] = React.useState(meeting.createdByMe);

  // A strict request must name a window — "exactly any time" is not a request.
  const bandOk = bandIsUsable(band, durationMinutes(meeting));
  const strictNeedsBand = meeting.createdByMe && strict && !band;
  const canSubmit = !!days && bandOk && !strictNeedsBand;

  function resetPicker() {
    setPicking(false);
    setDays(null);
    setBand(currentBand);
    setStrict(meeting.createdByMe);
  }

  function submitReschedule() {
    if (!canSubmit || !days) return;
    const pick: ReschedulePick = {
      startDate: days.start,
      endDate: days.end,
      // An attendee can never enforce a window; the backend strips it regardless.
      strict: meeting.createdByMe && strict,
    };
    if (band) {
      pick.timeStart = band.start;
      pick.timeEnd = band.end;
    }
    onReschedule!(meeting.id, pick);
    resetPicker();
  }

  return (
    <div
      className={cn(
        "comic-shadow-sm rounded-md border-2 border-border p-3",
        style.bg,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-comic-headline text-base uppercase tracking-wide">
              {meetingTitle(meeting)}
            </div>
            {typeLabel && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                {typeLabel}
              </span>
            )}
            {meeting.seriesId && (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-foreground">
                Recurring
              </span>
            )}
            {onOpenNegotiation && (
              <button
                type="button"
                title="Open the negotiation"
                onClick={() => onOpenNegotiation(meeting.id)}
                className="rounded-full border border-foreground/30 bg-background/60 px-2 py-0.5 font-mono text-[10px] hover:bg-muted"
              >
                {shortId(meeting.id)}
              </button>
            )}
            {meeting.rescheduledFrom && (
              <span
                title={`Replaces negotiation ${meeting.rescheduledFrom}`}
                className="rounded-full border border-foreground/30 bg-background/60 px-2 py-0.5 font-mono text-[10px]"
              >
                ↻ from {shortId(meeting.rescheduledFrom)}
              </span>
            )}
            {!!meeting.earlierAttempts && meeting.earlierAttempts > 0 && (
              <span className="rounded-full border border-foreground/30 bg-background/60 px-2 py-0.5 text-[10px] text-muted-foreground">
                {meeting.earlierAttempts === 1
                  ? "1 earlier attempt"
                  : `${meeting.earlierAttempts} earlier attempts`}
              </span>
            )}
            {meeting.pendingReschedule && (
              <span className="inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning-strong">
                Moving…
                {onAbandonReschedule && (
                  <button
                    type="button"
                    title="Stop moving it and keep this time"
                    onClick={() => onAbandonReschedule(meeting.id)}
                    className="rounded px-1 font-bold normal-case tracking-normal transition hover:bg-warning/20"
                  >
                    stop
                  </button>
                )}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <CalendarDays className="h-3 w-3" />
              {formatDateLabel(when)}
            </span>
            {meeting.start && meeting.end && (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Clock className="h-3 w-3" />
                {formatTime(meeting.start)} – {formatTime(meeting.end)}
                {duration !== null && ` · ${duration} min`}
              </span>
            )}
            {!meeting.start && meeting.state !== "ACCEPTED" && (
              <span className="text-muted-foreground italic">
                no slot decided yet
              </span>
            )}
          </div>
          {showStates ? (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <span className="text-[10px] uppercase text-muted-foreground">
                with
              </span>
              {meeting.participantStates.map((p) => {
                const chip = participantStateChip(p.state);
                return (
                  <span
                    key={p.name}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-1.5 text-[11px]",
                      chip.cls,
                    )}
                    title={p.required ? "Required" : "Optional"}
                  >
                    {p.name}
                    {!p.required && (
                      <span className="opacity-60">(opt)</span>
                    )}
                    <span className="opacity-70">· {chip.label}</span>
                  </span>
                );
              })}
            </div>
          ) : (
            meeting.participants.length > 0 && (
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <span className="text-[10px] uppercase text-muted-foreground">
                  with
                </span>
                {meeting.participants.map((name) => (
                  <span
                    key={name}
                    className="rounded border border-border bg-card px-1 text-[11px]"
                  >
                    {name}
                  </span>
                ))}
              </div>
            )
          )}
          {meeting.detail && meeting.detail.trim() && (
            <p className="mt-1.5 whitespace-pre-line text-xs text-foreground/80">
              {meeting.detail}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span
            className={cn(
              "rounded border-2 px-2 py-0.5 font-comic-headline text-[10px] uppercase tracking-wider",
              style.border,
              "bg-card",
            )}
          >
            {style.label}
          </span>
          {reschedulable && (
            <button
              type="button"
              onClick={() => setPicking((p) => !p)}
              className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              Reschedule
            </button>
          )}
          {cancellable && (
            <button
              type="button"
              onClick={() => onCancel!(meeting.id)}
              className="rounded-md border border-destructive/40 px-2 py-0.5 text-[11px] font-medium text-destructive transition hover:bg-destructive hover:text-destructive-foreground"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {meeting.failedReschedule && meeting.rescheduledByMe && (
        // Shown ONLY to the person who asked for the move — the "you asked for"
        // wording is theirs alone. Everyone else on the meeting gets a transient
        // toast from MeetingsPage instead; their meeting is simply unchanged.
        // The row's own Reschedule / Cancel buttons above are the only actions
        // needed — a failed attempt is terminal, so there's nothing extra to stop.
        <div className="mt-2 rounded border-2 border-destructive/40 bg-destructive/10 p-2">
          <p className="text-[11px] font-medium text-destructive-strong">
            {rescheduleGaveUpBanner(meeting.failedReschedule.state)}
          </p>
        </div>
      )}

      {reschedulable && picking && (
        <div className="mt-2 flex flex-col gap-2 border-t border-border pt-2">
          <DayRangeCalendar value={days} onChange={setDays} />

          <TimeRangePicker
            value={band}
            onChange={setBand}
            currentBand={currentBand}
            durationMinutes={durationMinutes(meeting)}
          />

          {/* Strictness is the meeting creator's to give. An attendee may say when
              they would rather meet; they may not pin a meeting they did not
              schedule. The backend strips priority from their request either way,
              so hiding the toggle is honesty, not a client-side rule. */}
          {meeting.createdByMe ? (
            <>
              <div className="inline-flex w-fit overflow-hidden rounded-md border border-input text-[11px]">
                <button
                  type="button"
                  onClick={() => setStrict(true)}
                  className={cn(
                    "px-2 py-1 font-medium transition",
                    strict
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-muted-foreground hover:text-foreground",
                  )}
                >
                  Exactly this
                </button>
                <button
                  type="button"
                  onClick={() => setStrict(false)}
                  className={cn(
                    "px-2 py-1 font-medium transition",
                    !strict
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-muted-foreground hover:text-foreground",
                  )}
                >
                  Around this
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {strict
                  ? "Everyone must be free in this exact window, whatever their usual hours. If nobody is, the meeting stays where it is."
                  : "The agents start here and widen into everyone's usual hours if they must."}
              </p>
            </>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              You're an attendee, so this travels as a preference.{" "}
              {meeting.createdBy ?? "the organiser"} arranged the meeting and their
              agent picks the time. The slot you're moving away from is ruled out for
              you either way.
            </p>
          )}

          {/* Plain-language summary of exactly what will be asked for. */}
          {days && (
            <p className="text-[11px] text-muted-foreground">
              Move to{" "}
              <span className="font-medium text-foreground">
                {band ? `${band.start}–${band.end}` : "any time"}
              </span>{" "}
              {days.start === days.end
                ? `on ${days.start}`
                : `each day from ${days.start} to ${days.end}`}
              {meeting.createdByMe &&
                band &&
                (strict
                  ? ". This exact window, or the meeting stays put."
                  : ". Preferred; the agents may widen it.")}
            </p>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!canSubmit}
              onClick={submitReschedule}
              className="rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-40"
            >
              Find new time
            </button>
            <button
              type="button"
              onClick={resetPicker}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {atImpasse && (
        <div className="mt-2 border-t border-warning/30 pt-2">
          <p className="text-[11px] font-medium text-warning-strong">
            No single time worked for everyone. Pick one to re-check with the group:
          </p>
          {resolvable ? (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {meeting.counterOptions.map((slot) => (
                <button
                  key={slot.start.toISOString()}
                  type="button"
                  onClick={() => onResolve!(meeting.id, [slot.start.toISOString()])}
                  className="rounded-md border border-warning/40 bg-warning/10 px-2 py-1 text-[11px] font-medium text-warning-strong transition hover:bg-warning/20"
                >
                  {formatDateLabel(slot.start)} {formatTime(slot.start)}
                </button>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-[11px] text-muted-foreground">
              No alternative times were offered. Try rescheduling to a fresh window.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
